import { Navigate, Route, Routes } from 'react-router-dom';
import { useVersionCheck } from './hooks/useVersionCheck';
import { LoginPage } from './pages/LoginPage';
import { PublicLandingPage } from './pages/PublicLandingPage';
import { PublicCompetitionPage } from './pages/public/PublicCompetitionPage';
import { BoardPage } from './pages/public/BoardPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ProtectedRoute } from './router/ProtectedRoute';
import { SuperAdminLayout } from './components/layout/SuperAdminLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { RecorderLayout } from './components/layout/RecorderLayout';
import { SuperAdminDashboardPage } from './pages/superadmin/SuperAdminDashboardPage';
import { UnitsPage } from './pages/superadmin/UnitsPage';
import { UsersPage } from './pages/superadmin/UsersPage';
import { SettingsPage } from './pages/superadmin/SettingsPage';
import { LayoutVisibilityPage } from './pages/superadmin/LayoutVisibilityPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { CompetitionsPage } from './pages/admin/CompetitionsPage';
import { CompetitionDetailPage } from './pages/admin/CompetitionDetailPage';
import { TemplatesPage } from './pages/admin/TemplatesPage';
import { RecorderHomePage } from './pages/recorder/RecorderHomePage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { SuperAdminCompetitionsPage } from './pages/superadmin/SuperAdminCompetitionsPage';

export const App = () => {
  useVersionCheck();

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<PublicLandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route path="/s/:shortCode" element={<PublicCompetitionPage />} />
      <Route path="/board/:token" element={<BoardPage />} />

      {/* SUPER_ADMIN */}
      <Route
        path="/superadmin"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <SuperAdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SuperAdminDashboardPage />} />
        <Route path="units" element={<UnitsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="competitions" element={<SuperAdminCompetitionsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="layout-visibility" element={<LayoutVisibilityPage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
      </Route>

      {/* UNIT_ADMIN */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'UNIT_ADMIN']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="competitions" element={<CompetitionsPage />} />
        <Route path="competitions/:id" element={<CompetitionDetailPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="audit" element={<AdminAuditPage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
      </Route>

      {/* RECORDER */}
      <Route
        path="/recorder"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'UNIT_ADMIN', 'RECORDER']}>
            <RecorderLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RecorderHomePage />} />
        <Route path="competitions/:id" element={<CompetitionDetailPage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
      </Route>

      {/* Backwards-compat: staré /profile/change-password přesměruj podle role */}
      <Route
        path="/profile/change-password"
        element={<ChangePasswordRedirect />}
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * Redirect na layout-specific change-password podle aktualni role.
 * Udrzuje uzivatele v jejich "pracovnim" layoutu a Cancel tedy funguje plne.
 */
const ChangePasswordRedirect = () => {
  // Tento komponent se rendruje uvnitr ProtectedRoute nemá, takze musime sami check auth
  return (
    <ProtectedRoute>
      <RoleBasedChangePwdRedirect />
    </ProtectedRoute>
  );
};

import { useAuthStore } from './store/authStore';

const RoleBasedChangePwdRedirect = () => {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  const target =
    user.role === 'SUPER_ADMIN'
      ? '/superadmin/change-password'
      : user.role === 'UNIT_ADMIN'
        ? '/admin/change-password'
        : '/recorder/change-password';
  return <Navigate to={target} replace />;
};
